<template>
  <div class="customer-materials-quote-list">
    <div v-if="loading">
      <CircleLoader></CircleLoader>
    </div>
    <div v-if="!loading">
      <div v-if="quotes.length">
        <b-table
          striped
          :sticky-header="true"
          hover
          @row-clicked="goToQuote"
          :items="quotes"
          :fields="fields"
        >
          <template #cell(shortId)="data">
            <strong class="text-primary">{{ data.item.shortId }}</strong>
          </template>

          <template #cell(amount)="data">
            <span v-if="data.item.amount" class="text-success font-weight-bold">
              ${{ formatCurrency(data.item.amount) }}
            </span>
            <span v-else class="text-muted">-</span>
          </template>

          <template #cell(expirationDate)="data">
            <div v-if="data.item.expirationDate">
              {{ formatDate(data.item.expirationDate) }}
              <div v-if="isExpired(data.item.expirationDate)" class="text-danger small">
                <i class="fas fa-exclamation-triangle"></i> Expired
              </div>
              <div v-else-if="isExpiringSoon(data.item.expirationDate)" class="text-warning small">
                <i class="fas fa-clock"></i> Expires Soon
              </div>
            </div>
            <span v-else class="text-muted">-</span>
          </template>

          <template #cell(status)="data">
            <b-badge :variant="getStatusVariant(data.item.status)">
              {{ data.item.status }}
            </b-badge>
          </template>

          <template #cell(createdAt)="data">
            {{ formatDate(data.item.createdAt) }}
          </template>
        </b-table>
      </div>
      <div v-if="!quotes.length">- No materials quotes -</div>
    </div>

    <!-- View Quote Modal -->
    <b-modal
      v-model="showViewModal"
      :title="`Materials Quote ${viewingQuote && viewingQuote.shortId ? viewingQuote.shortId : ''}`"
      size="xl"
      hide-footer
      @hidden="resetModal"
    >
      <div v-if="viewingQuote" class="quote-view">
        <!-- Quote Header with Customer Info -->
        <div class="customer-info-section mb-4">
          <div class="customer-header">
            <h6 class="customer-title">
              <i class="fas fa-user-circle me-2 text-primary"></i>
              Customer Information
            </h6>
          </div>
          
          <!-- Customer Details -->
          <div v-if="viewingQuote.customer && viewingQuote.customer.name" class="customer-details">
            <div class="customer-card">
              <div class="customer-main-info">
                <div class="customer-name">
                  <strong>{{ viewingQuote.customer.name }}</strong>
                </div>
                <div v-if="viewingQuote.customer && viewingQuote.customer.company" class="customer-company text-muted">
                  {{ viewingQuote.customer.company }}
                </div>
              </div>
              <div class="customer-contact-info">
                <div v-if="viewingQuote.customer && viewingQuote.customer.email" class="contact-item">
                  <i class="fas fa-envelope text-muted me-2"></i>
                  <span>{{ viewingQuote.customer.email }}</span>
                </div>
                <div v-if="viewingQuote.customer && viewingQuote.customer.phone" class="contact-item">
                  <i class="fas fa-phone text-muted me-2"></i>
                  <span>{{ viewingQuote.customer.phone }}</span>
                </div>
              </div>
            </div>
          </div>
          
          <!-- No Customer State -->
          <div v-else class="no-customer-state">
            <div class="text-center py-3">
              <i class="fas fa-user-slash fa-2x text-muted mb-2"></i>
              <h6 class="text-muted">No Customer</h6>
              <p class="text-muted mb-0">This quote was created without customer information</p>
            </div>
          </div>
        </div>

        <!-- Quote Details Section -->
        <div class="quote-details-section mb-4">
          <div class="quote-header-info">
            <!-- Title and Status Row -->
            <div class="row align-items-start mb-2">
              <div class="col-md-9">
                <h4 class="quote-title mb-1">{{ viewingQuote.title }}</h4>
                <p v-if="viewingQuote.description" class="quote-description text-muted mb-0">{{ viewingQuote.description }}</p>
              </div>
              <div class="col-md-3 text-end">
                <b-badge :variant="getStatusVariant(viewingQuote.status)" class="status-badge">
                  {{ viewingQuote.status }}
                </b-badge>
              </div>
            </div>
            
            <!-- Details Grid -->
            <div class="row details-grid">
              <div class="col-6 col-md-3">
                <div class="detail-item">
                  <span class="detail-label">Quote Type:</span>
                  <span class="detail-value">Materials Quote</span>
                </div>
              </div>
              <div class="col-6 col-md-3">
                <div class="detail-item">
                  <span class="detail-label">Quote Amount:</span>
                  <span class="detail-value">${{ formatCurrency(viewingQuote.amount || 0) }}</span>
                </div>
              </div>
              <div class="col-6 col-md-3">
                <div class="detail-item">
                  <span class="detail-label">Quote Date:</span>
                  <span class="detail-value">{{ formatDate(viewingQuote.createdAt) }}</span>
                </div>
              </div>
              <div class="col-6 col-md-3">
                <div class="detail-item">
                  <span class="detail-label">Created By:</span>
                  <span class="detail-value">{{ getUserDisplayName(viewingQuote.createdBy) }}</span>
                </div>
              </div>
            </div>
            
            <!-- Second Row for Expiration if exists -->
            <div v-if="viewingQuote.expirationDate" class="row details-grid-secondary">
              <div class="col-6 col-md-3">
                <div class="detail-item">
                  <span class="detail-label">Expires:</span>
                  <span class="detail-value" :class="getExpirationClass(viewingQuote.expirationDate)">
                    {{ formatDate(viewingQuote.expirationDate) }}
                    <i v-if="isExpired(viewingQuote.expirationDate)" class="fas fa-exclamation-triangle ms-1"></i>
                    <i v-else-if="isExpiringSoon(viewingQuote.expirationDate)" class="fas fa-clock ms-1"></i>
                  </span>
                </div>
              </div>
            </div>
            
            <!-- QuickBooks Numbers (if any) -->
            <div v-if="viewingQuote.quoteNumbers && viewingQuote.quoteNumbers.length > 0" class="quickbooks-section">
              <span class="detail-label">QuickBooks Quote Numbers:</span>
              <div class="quote-numbers-container">
                <span v-for="quoteNum in viewingQuote.quoteNumbers" :key="quoteNum" class="quote-number-badge">
                  {{ quoteNum }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Tabs for Follow-up and Details -->
        <b-tabs v-model="activeModalTab" class="quote-tabs">
          <!-- Next Follow-up Tab -->
          <b-tab title="📅 Next Follow-up">
            <div class="tab-content-padding">
              <div class="row">
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Status</label>
                    <b-form-select
                      v-model="followUpForm.status"
                      :options="quoteStatusOptions"
                    />
                  </div>
                  
                  <div class="mb-3">
                    <label class="form-label">Priority</label>
                    <b-form-select
                      v-model="followUpForm.priority"
                      :options="priorityOptions"
                    />
                  </div>
                </div>
                
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Next Follow-up Date</label>
                    <div class="follow-up-date-controls mb-2">
                      <button 
                        type="button" 
                        class="btn btn-sm btn-outline-primary me-2"
                        @click="setFollowUpDate(7)"
                      >
                        1 Week
                      </button>
                      <button 
                        type="button" 
                        class="btn btn-sm btn-outline-primary me-2"
                        @click="setFollowUpDate(14)"
                      >
                        2 Weeks
                      </button>
                      <button 
                        type="button" 
                        class="btn btn-sm btn-outline-primary"
                        @click="setFollowUpDate(30)"
                      >
                        1 Month
                      </button>
                    </div>
                    <b-form-input
                      v-model="followUpForm.nextFollowUpDate"
                      type="datetime-local"
                    />
                  </div>
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label">Follow-up Notes</label>
                <b-form-textarea
                  v-model="followUpForm.note"
                  rows="4"
                  placeholder="Add notes about this follow-up..."
                />
              </div>
            </div>
          </b-tab>

          <!-- Past Follow-ups Tab -->
          <b-tab title="📋 Past Follow-ups">
            <div class="tab-content-padding">
              <FollowUpTimeline :follow-ups="viewingQuote.followUps" />
            </div>
          </b-tab>
        </b-tabs>

        <!-- Modal Actions -->
        <div class="modal-actions mt-4">
          <div class="d-flex justify-content-between">
            <div>
              <button class="btn btn-success me-2" @click="convertToOrder">
                <i class="fas fa-exchange-alt me-1"></i> Convert to Materials Order
              </button>
              <button class="btn btn-primary me-2" @click="editQuote">
                <i class="fas fa-edit me-1"></i> Edit Quote
              </button>
              <button 
                class="btn btn-info me-2" 
                @click="submitFollowUp"
                :disabled="submittingFollowUp || !followUpForm.note.trim()"
              >
                <span v-if="submittingFollowUp">
                  <i class="fas fa-spinner fa-spin me-1"></i> Saving...
                </span>
                <span v-else>
                  <i class="fas fa-calendar-plus me-1"></i> Save Follow-up
                </span>
              </button>
            </div>
            <div>
              <button class="btn btn-secondary" @click="showViewModal = false">
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import _ from 'lodash'
import CircleLoader from '@/components/CircleLoader.vue'
import FollowUpTimeline from '@/components/FollowUpTimeline.vue'
import axios from 'axios'
import moment from 'moment'

export default {
  name: 'CustomerMaterialsQuoteList',
  components: { CircleLoader, FollowUpTimeline },
  props: {
    customer: {
      type: Object,
      required: true,
    },
  },
  data: function () {
    return {
      quotes: [],
      loading: true,
      showViewModal: false,
      viewingQuote: null,
      activeModalTab: 0,
      submittingFollowUp: false,
      followUpForm: {
        status: 'IN_PROGRESS',
        priority: 'MEDIUM',
        nextFollowUpDate: '',
        note: ''
      },
      quoteStatusOptions: [
        { value: 'IN_PROGRESS', text: 'In Progress' },
        { value: 'NO_FOLLOW_UP', text: 'No Follow-up' },
        { value: 'CONVERTED', text: 'Converted' },
        { value: 'LOST', text: 'Lost' }
      ],
      priorityOptions: [
        { value: 'LOW', text: 'Low' },
        { value: 'MEDIUM', text: 'Medium' },
        { value: 'HIGH', text: 'High' },
        { value: 'URGENT', text: 'Urgent' }
      ],
      fields: [
        {
          key: 'shortId',
          label: 'Quote #',
          class: ['clickable-row'],
          sortable: true,
        },
        {
          key: 'title',
          label: 'Title',
          class: ['clickable-row'],
          sortable: true,
        },
        {
          key: 'amount',
          label: 'Amount',
          class: ['clickable-row', 'text-right'],
          sortable: true,
        },
        {
          key: 'expirationDate',
          label: 'Expires',
          class: ['clickable-row'],
          sortable: true,
        },
        {
          key: 'status',
          label: 'Status',
          class: ['clickable-row'],
          sortable: true,
        },
        {
          key: 'createdAt',
          label: 'Created',
          class: ['clickable-row'],
          sortable: true,
        },
      ],
    }
  },
  computed: {},
  mounted() {
    this.getQuotes()
  },
  methods: {
    async goToQuote(quote, index, e) {
      if (e.metaKey) {
        window.open('/app/sales/materials?viewQuote=' + quote.shortId, '_blank')
      } else {
        // Fetch full quote details and show modal
        await this.viewQuote(quote.shortId)
      }
    },

    async viewQuote(shortId) {
      try {
        const response = await axios.get(`/services/v2/materials-quotes/${shortId}`)
        this.viewingQuote = response.data.quote
        
        // Initialize follow-up form
        this.followUpForm = {
          status: this.viewingQuote.quoteStatus || 'IN_PROGRESS',
          priority: this.viewingQuote.followUpPriority || 'MEDIUM',
          nextFollowUpDate: this.viewingQuote.nextFollowUpDate
            ? moment(this.viewingQuote.nextFollowUpDate).format('YYYY-MM-DDTHH:mm')
            : moment().add(7, 'days').format('YYYY-MM-DDTHH:mm'), // Default to 1 week
          note: ''
        }
        
        this.activeModalTab = 0
        this.showViewModal = true
      } catch (error) {
        console.error('Error loading quote details:', error)
        this.$bvToast.toast('Error loading quote details', {
          title: 'Error',
          variant: 'danger'
        })
      }
    },

    resetModal() {
      this.viewingQuote = null
    },

    setFollowUpDate(days) {
      const date = moment().add(days, 'days').format('YYYY-MM-DDTHH:mm')
      this.followUpForm.nextFollowUpDate = date
    },

    async submitFollowUp() {
      if (!this.viewingQuote || !this.followUpForm.note.trim()) {
        this.$bvToast.toast('Please add a follow-up note', {
          title: 'Missing Information',
          variant: 'warning'
        })
        return
      }

      this.submittingFollowUp = true
      try {
        const followUpData = {
          quoteStatus: this.followUpForm.status,
          followUpPriority: this.followUpForm.priority,
          nextFollowUpDate: this.followUpForm.nextFollowUpDate,
          note: this.followUpForm.note.trim()
        }

        const response = await axios.put(
          `/services/v2/materials-quotes/${this.viewingQuote.shortId}/follow-up`,
          followUpData
        )

        // Update the viewing quote with the response data
        if (response.data.quote) {
          this.viewingQuote = response.data.quote
        }

        // Reset the form
        this.followUpForm.note = ''
        
        // Show success message
        this.$bvToast.toast('Follow-up saved successfully', {
          title: 'Success',
          variant: 'success'
        })

        // Refresh the quotes list
        await this.getQuotes()
      } catch (error) {
        console.error('Error submitting follow-up:', error)
        this.$bvToast.toast('Error saving follow-up', {
          title: 'Error',
          variant: 'danger'
        })
      } finally {
        this.submittingFollowUp = false
      }
    },

    getUserDisplayName(email) {
      if (!email) return 'Unknown'
      const atIndex = email.indexOf('@')
      if (atIndex === -1) return email
      
      const namePart = email.substring(0, atIndex)
      return namePart.split('.').map(part => 
        part.charAt(0).toUpperCase() + part.slice(1)
      ).join(' ')
    },

    editQuote() {
      // Navigate to materials quotes page with edit mode
      this.showViewModal = false
      this.$router.push({
        name: 'MaterialsQuotesPage',
        params: { editQuote: this.viewingQuote.shortId }
      })
    },

    convertToOrder() {
      // Close the modal and navigate to create order with prefilled data
      this.showViewModal = false
      
      const orderData = {
        jobName: `Materials Order - ${this.viewingQuote.title}`,
        type: 'MATERIALS',
        deliveryMethod: 'PICKUP',
        productId: this.viewingQuote.shortId,
        totalAmount: this.viewingQuote.amount || 0,
        customer: {
          name: this.viewingQuote.customer && this.viewingQuote.customer.name ? this.viewingQuote.customer.name : '',
          email: this.viewingQuote.customer && this.viewingQuote.customer.email ? this.viewingQuote.customer.email : '',
          phone: this.viewingQuote.customer && this.viewingQuote.customer.phone ? this.viewingQuote.customer.phone : '',
          company: this.viewingQuote.customer && this.viewingQuote.customer.company ? this.viewingQuote.customer.company : '',
          vip: false
        },
        description: this.viewingQuote.description || '',
        materialsQuoteId: this.viewingQuote.shortId,
        materialsQuoteNumbers: this.viewingQuote.quoteNumbers || [],
        notes: `Created from Materials Quote ${this.viewingQuote.shortId}${this.viewingQuote.description ? '\n\nOriginal Description:\n' + this.viewingQuote.description : ''}`
      }
      
      this.$router.push({
        name: 'CreateOrderPage',
        params: {
          prefilledData: orderData
        }
      })
      
      this.$bvToast.toast(`Navigating to create order page with data from Materials Quote ${this.viewingQuote.shortId}`, {
        title: 'Converting to Materials Order',
        variant: 'info',
        autoHideDelay: 3000
      })
    },

    async getQuotes() {
      try {
        let customerId = this.customer._id || this.customer.id
        let response = await axios.get(
          '/services/v2/materials-quotes?customerId=' + encodeURIComponent(customerId),
        )

        this.quotes = response.data.quotes.map(quote => {
          return {
            shortId: quote.shortId,
            title: quote.title,
            amount: quote.amount,
            expirationDate: quote.expirationDate,
            status: quote.status,
            createdAt: quote.createdAt,
          }
        })
      } catch (error) {
        console.log('Error loading materials quotes: ', error)
      }
      this.loading = false
    },

    getStatusVariant(status) {
      const variants = {
        'DRAFT': 'secondary',
        'OPEN': 'info',
        'SOLD': 'success',
        'REJECTED': 'danger',
        'EXPIRED': 'warning'
      }
      return variants[status] || 'secondary'
    },

    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleDateString()
    },

    formatCurrency(amount) {
      if (!amount && amount !== 0) return '0.00'
      return parseFloat(amount).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },

    isExpired(expirationDate) {
      if (!expirationDate) return false
      return moment(expirationDate).isBefore(moment(), 'day')
    },

    isExpiringSoon(expirationDate) {
      if (!expirationDate) return false
      const expDate = moment(expirationDate)
      const today = moment()
      const daysUntilExpiry = expDate.diff(today, 'days')
      return daysUntilExpiry >= 0 && daysUntilExpiry <= 7
    },

    getExpirationClass(expirationDate) {
      if (this.isExpired(expirationDate)) {
        return 'text-danger'
      } else if (this.isExpiringSoon(expirationDate)) {
        return 'text-warning'
      }
      return ''
    },
  },
}
</script>

<style lang="scss" scoped>
.customer-materials-quote-list {
  .clickable-row {
    cursor: pointer;
  }
}

// Copy styles from MaterialsQuotesPage for consistency
.quote-view {
  .customer-info-section {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    border-radius: 12px;
    padding: 1.5rem;
    border: 1px solid #dee2e6;
    box-shadow: 0 2px 4px rgba(0,0,0,0.05);

    .customer-title {
      color: #495057;
      font-weight: 600;
      margin-bottom: 1rem;
      font-size: 1rem;
    }

    .customer-card {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      flex-wrap: wrap;
      gap: 1rem;

      .customer-main-info {
        flex: 1;
        min-width: 200px;

        .customer-name {
          font-size: 1.25rem;
          font-weight: 600;
          color: #2c3e50;
          margin-bottom: 0.25rem;
        }

        .customer-company {
          font-size: 0.95rem;
          color: #6c757d;
          font-style: italic;
        }
      }

      .customer-contact-info {
        flex: 1;
        min-width: 200px;

        .contact-item {
          display: flex;
          align-items: center;
          margin-bottom: 0.5rem;
          font-size: 0.9rem;
          color: #495057;

          i {
            width: 18px;
            color: #6c757d;
          }
        }
      }
    }

    .no-customer-state {
      text-align: center;
      padding: 2rem;
      color: #6c757d;
    }
  }

  .quote-details-section {
    .quote-header-info {
      .quote-title {
        color: #2c3e50;
        font-weight: 600;
        font-size: 1.5rem;
      }

      .quote-description {
        font-size: 1rem;
        line-height: 1.4;
      }

      .status-badge {
        font-size: 0.875rem;
        padding: 0.5rem 1rem;
        font-weight: 600;
      }

      .details-grid, .details-grid-secondary {
        margin-top: 1.5rem;
        padding-top: 1.5rem;
        border-top: 1px solid #e9ecef;

        .detail-item {
          margin-bottom: 1rem;

          .detail-label {
            display: block;
            font-size: 0.8rem;
            font-weight: 600;
            color: #6c757d;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: 0.25rem;
          }

          .detail-value {
            display: block;
            font-size: 1rem;
            font-weight: 500;
            color: #2c3e50;
          }
        }
      }

      .quickbooks-section {
        margin-top: 1.5rem;
        padding-top: 1.5rem;
        border-top: 1px solid #e9ecef;

        .detail-label {
          display: block;
          font-size: 0.8rem;
          font-weight: 600;
          color: #6c757d;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 0.75rem;
        }

        .quote-numbers-container {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;

          .quote-number-badge {
            background: #e3f2fd;
            color: #1976d2;
            padding: 0.375rem 0.75rem;
            border-radius: 6px;
            font-size: 0.875rem;
            font-weight: 500;
            border: 1px solid #bbdefb;
          }
        }
      }
    }
  }

  .quote-tabs {
    margin-top: 2rem;

    .tab-content-padding {
      padding: 1.5rem 0;
    }

    .form-label {
      font-weight: 600;
      color: #495057;
      margin-bottom: 0.5rem;
    }

    .follow-up-date-controls {
      display: flex;
      gap: 0.5rem;
      flex-wrap: wrap;
    }
  }

  .modal-actions {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    margin: 2rem -1.5rem -1.5rem -1.5rem;
    padding: 1.5rem;
    border-top: 1px solid #dee2e6;
    border-radius: 0 0 0.375rem 0.375rem;

    .btn {
      font-weight: 500;
      border-radius: 6px;
      padding: 0.5rem 1rem;

      i {
        margin-right: 0.5rem;
      }
    }
  }
}
</style>
